import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { AlertTriangle, CheckCircle2, Crown, Lock, ShieldOff, XCircle } from 'lucide-react';
import { trainingApi } from '../../services/api';

/**
 * The Model Training dashboards (EP-26 FE-4, plan §7 and §B6): the pages Abri ran
 * from APTO-Lab-Console on his machine, rebuilt on production read models. Every
 * number comes from a store view; the server says when a store cannot be read.
 */
type D = Record<string, any>;
const money = (n: number | null | undefined) => (n == null ? '—' : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 })}`);
const num = (n: number | null | undefined) => (n == null ? '—' : Number(n).toLocaleString());

export function Tile({ label, value, sub, tone }: { label: string; value: React.ReactNode; sub?: React.ReactNode; tone?: 'warn' | 'bad' }) {
  const t = tone === 'bad' ? 'border-red-200 dark:border-red-800' : tone === 'warn' ? 'border-amber-200 dark:border-amber-800' : 'border-gray-200 dark:border-gray-700';
  return (
    <div className={`rounded-lg border ${t} p-3`}>
      <div className="text-xs uppercase tracking-wide text-gray-500">{label}</div>
      <div className="mt-1 text-lg font-medium text-gray-900 dark:text-gray-100 tabular-nums">{value}</div>
      {sub ? <div className="text-xs text-gray-500 mt-0.5">{sub}</div> : null}
    </div>
  );
}

function Table({ cols, rows, max = 500 }: { cols: { h: string; r: (x: D) => React.ReactNode; right?: boolean }[]; rows: D[]; max?: number }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 dark:bg-gray-800 text-xs uppercase text-gray-500">
          <tr>{cols.map((c) => <th key={c.h} className={`p-2 ${c.right ? 'text-right' : 'text-left'}`}>{c.h}</th>)}</tr>
        </thead>
        <tbody>
          {rows.slice(0, max).map((x, i) => (
            <tr key={x.id || x.ref || x.atom || i} className="border-t border-gray-100 dark:border-gray-700">
              {cols.map((c) => <td key={c.h} className={`p-2 text-gray-800 dark:text-gray-200 ${c.right ? 'text-right tabular-nums' : ''}`}>{c.r(x)}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length > max ? <div className="p-2 text-xs text-gray-500">Showing {max} of {rows.length.toLocaleString()}; filter to narrow.</div> : null}
    </div>
  );
}

const H = ({ children }: { children: React.ReactNode }) => <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mt-2">{children}</h2>;

// ---- Runs and spend --------------------------------------------------------------
export function RunsBoard({ d }: { d: D }) {
  const [stage, setStage] = useState('');
  const stages = useMemo(() => [...new Set((d.runs as D[]).map((r) => r.stage).filter(Boolean))].sort(), [d.runs]);
  const runs = (d.runs as D[]).filter((r) => !stage || r.stage === stage);
  const thisMonth = (d.by_month as D[])[0];
  return (
    <div className="space-y-4">
      <div className="grid gap-3 grid-cols-2 md:grid-cols-5">
        <Tile label="Runs" value={num(d.total_runs)} sub={Object.entries(d.by_status || {}).map(([k, v]) => `${k} ${v}`).join(' · ')} />
        <Tile label={`GPU estimate ${thisMonth?.month || ''}`} value={money(thisMonth?.cost_usd)} sub={`${thisMonth?.runs ?? 0} runs`} />
        <Tile label="Running now" value={d.running.length} />
        <Tile label="Open over 24 h" value={d.stale_open.length} tone={d.stale_open.length ? 'warn' : undefined} sub="run docs probably never closed" />
        <Tile label="Rate unknown" value={d.rate_unknown} sub="GPU not in the list-rate table" />
      </div>
      {(d.stale_open as D[]).length > 0 && <StaleRuns rows={d.stale_open} />}
      <H>GPU spend estimate by month</H>
      <div className="h-56 rounded-lg border border-gray-200 dark:border-gray-700 p-2">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={[...(d.by_month as D[])].reverse()}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
            <XAxis dataKey="month" fontSize={11} /><YAxis fontSize={11} />
            <Tooltip formatter={(v: any) => money(v)} />
            <Bar dataKey="cost_usd" name="Estimate (USD)" fill="#6366f1" />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <div><H>By box</H><Table rows={d.by_box} cols={[{ h: 'Box', r: (x) => x.box }, { h: 'Runs', r: (x) => x.runs, right: true }, { h: 'Hours', r: (x) => x.hours, right: true }, { h: 'Estimate', r: (x) => money(x.cost_usd), right: true }]} /></div>
        <div><H>Mining (teacher calls, metered equivalent)</H>
          {d.mining_equivalent_by_month ? <Table rows={d.mining_equivalent_by_month} cols={[{ h: 'Month', r: (x) => x.month }, { h: 'Equivalent', r: (x) => money(x.cost_usd), right: true }]} />
            : <p className="text-xs text-gray-500">Gold store not readable.</p>}
        </div>
      </div>
      {(d.by_campaign as D[]).length > 0 && (<><H>By campaign and round</H>
        <Table rows={d.by_campaign} cols={[{ h: 'Campaign', r: (x) => x.campaign }, { h: 'Round', r: (x) => x.round || '—' }, { h: 'Runs', r: (x) => x.runs, right: true }, { h: 'Cost', r: (x) => money(x.cost_usd), right: true }]} /></>)}
      <div className="flex items-center gap-2"><H>Run ledger</H>
        <select value={stage} onChange={(e) => setStage(e.target.value)} className="ml-auto px-2 py-1 text-xs border rounded dark:bg-gray-700 dark:border-gray-600">
          <option value="">All stages</option>{stages.map((s) => <option key={s}>{s}</option>)}
        </select>
      </div>
      <Table rows={runs} max={300} cols={[
        { h: 'Started', r: (x) => (x.started || '').replace('T', ' ').slice(0, 16) }, { h: 'Run', r: (x) => <code className="text-xs">{x.id}</code> },
        { h: 'Stage', r: (x) => x.stage }, { h: 'Status', r: (x) => x.status }, { h: 'Box', r: (x) => x.box || '—' },
        { h: 'Hours', r: (x) => x.hours ?? '—', right: true }, { h: 'Cost', r: (x) => (x.cost_usd == null ? '—' : `${money(x.cost_usd)}${x.cost_kind === 'estimate' ? ' est.' : ''}`), right: true },
        { h: 'Campaign', r: (x) => x.campaign ? `${x.campaign}${x.round ? ' / ' + x.round : ''}` : '—' }]} />
      <ul className="text-xs text-gray-500 list-disc pl-5">{(d.notes as string[]).map((n) => <li key={n}>{n}</li>)}<li>Rates: {d.rate_source}.</li></ul>
    </div>
  );
}

// ---- Models ------------------------------------------------------------------------
export function ModelsBoard({ d }: { d: D }) {
  return (
    <div className="space-y-4">
      <div className="text-sm flex items-center gap-2 text-gray-800 dark:text-gray-200">
        <Crown className="w-4 h-4 text-amber-500" />
        Champion: {d.champion ? <code>{d.champion}</code> : <span className="text-gray-500">none</span>} <span className="text-xs text-gray-500">({d.champion_source})</span>
        {Object.keys(d.champions || {}).length > 0 && (
          <span className="block text-xs mt-1">By stream: {Object.entries(d.champions as Record<string, D>).map(([k, c]) => <span key={k} className="mr-3">{k}: <code>{c.model}</code> <span className="text-gray-500">({c.source})</span></span>)}</span>)}
      </div>
      <Tile label="Model documents" value={num(d.total)} />
      {d.champion && (
        <div>
          <H>Champion within-1 per layer (worst seed)</H>
          {(d.champion_layers || []).length === 0
            ? <p className="text-xs text-gray-500">No eval verdict recorded for the champion{d.champion_verdict ? '' : ' (manifest:eval-verdict-<model> is missing)'}.</p>
            : <Table rows={d.champion_layers} cols={[{ h: 'Layer', r: (x) => x.layer },
                { h: 'Within-1', r: (x) => x.within_1 ?? '—', right: true }, { h: 'Abstention recall', r: (x) => x.abstention_recall ?? '—', right: true },
                { h: 'Unsafe', r: (x) => x.unsafe ?? '—', right: true }, { h: 'n', r: (x) => num(x.n), right: true },
                { h: 'Verdict', r: (x) => <span className={x.verdict === 'pass' ? 'text-emerald-600' : x.verdict ? 'text-red-600' : 'text-gray-400'}>{x.verdict || '—'}{(x.failed_bars || []).length ? ` (${x.failed_bars.join(', ')})` : ''}</span> }]} />}
        </div>)}
      {(d.by_round as D[]).map((g) => (
        <div key={g.round}>
          <H>{g.round}</H>
          <Table rows={g.models} cols={[
            { h: 'Model', r: (x) => <span className="flex items-center gap-1">{x.champion ? <Crown className="w-3.5 h-3.5 text-amber-500" /> : null}<code className="text-xs">{x.id}</code></span> },
            { h: 'Base', r: (x) => x.base_model || '—' }, { h: 'Seed', r: (x) => x.seed ?? '—', right: true },
            { h: 'Completed', r: (x) => (x.completed_at || '').slice(0, 10) || '—' },
            { h: 'Eval', r: (x) => x.verdict ? <span className={x.verdict.verdict === 'pass' ? 'text-emerald-600' : 'text-red-600'}>{x.verdict.verdict}{x.verdict.failed_layers?.length ? ` (${x.verdict.failed_layers.join(', ')})` : ''}</span> : <span className="text-gray-400">no verdict</span> },
            { h: 'Adapter sha', r: (x) => x.sha256 ? <code className="text-xs">{String(x.sha256).slice(0, 12)}</code> : <span className="text-amber-600">none</span> }]} />
        </div>
      ))}
      <H>Eval verdicts (every layer at its worst seed)</H>
      <Table rows={d.verdicts} cols={[{ h: 'Model', r: (x) => x.model }, { h: 'Verdict', r: (x) => x.verdict }, { h: 'Seeds', r: (x) => x.seeds ?? '—', right: true },
        { h: 'Within-1', r: (x) => x.within_1 ?? '—', right: true }, { h: 'Abstention recall', r: (x) => x.abstention_recall ?? '—', right: true },
        { h: 'Unsafe', r: (x) => x.unsafe ?? '—', right: true }, { h: 'Failed layers', r: (x) => (x.failed_layers || []).join(', ') || '—' }]} />
    </div>
  );
}

// ---- Atoms -------------------------------------------------------------------------
export function AtomsBoard({ d }: { d: D }) {
  const [layer, setLayer] = useState(''); const [family, setFamily] = useState(''); const [q, setQ] = useState('');
  const atoms = (d.atoms as D[]).filter((a) => (!layer || a.layer === layer) && (!family || a.family === family)
    && (!q || `${a.ref} ${a.name || ''}`.toLowerCase().includes(q.toLowerCase())));
  const sum = (k: string) => atoms.reduce((s, a) => s + (a[k] || 0), 0);
  return (
    <div className="space-y-4">
      <div className="grid gap-3 grid-cols-2 md:grid-cols-5">
        <Tile label="Released atoms" value={num(atoms.length)} sub={Object.entries(d.status_counts || {}).map(([k, v]) => `${k} ${v}`).join(' · ')} />
        <Tile label="At evidence floor" value={num(atoms.filter((a) => !a.below_floor).length)} sub={`≥ ${d.evidence_floor} qualified learners`} />
        <Tile label="Grounded" value={num(atoms.filter((a) => a.grounded).length)} />
        <Tile label="With gold" value={num(atoms.filter((a) => a.gold > 0).length)} sub={`${num(sum('gold'))} active gold rows`} />
        <Tile label="Declined cases ≥ 5" value={num(atoms.filter((a) => a.minted >= 5).length)} />
      </div>
      <div className="flex flex-wrap gap-2">
        <select value={layer} onChange={(e) => setLayer(e.target.value)} className="px-2 py-1 text-xs border rounded dark:bg-gray-700 dark:border-gray-600"><option value="">All layers</option>{(d.layers as string[]).map((x) => <option key={x}>{x}</option>)}</select>
        <select value={family} onChange={(e) => setFamily(e.target.value)} className="px-2 py-1 text-xs border rounded dark:bg-gray-700 dark:border-gray-600"><option value="">All families</option>{(d.families as string[]).map((x) => <option key={x}>{x}</option>)}</select>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find atom" className="px-2 py-1 text-xs border rounded dark:bg-gray-700 dark:border-gray-600" />
      </div>
      <Table rows={atoms} max={400} cols={[
        { h: 'Atom', r: (a) => <span><code className="text-xs">{a.ref}</code>{a.name ? <span className="block text-xs text-gray-500">{a.name}</span> : null}</span> },
        { h: 'Layer', r: (a) => a.layer }, { h: 'Family', r: (a) => a.family || '—' },
        { h: 'Card', r: (a) => a.measured ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <span className="text-xs text-amber-600">no measurement spec</span> },
        { h: 'Qualified', r: (a) => <span className={a.below_floor ? 'text-red-600' : ''}>{num(a.qualified)}</span>, right: true },
        { h: 'Grounded', r: (a) => a.grounded ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <XCircle className="w-4 h-4 text-gray-400" /> },
        { h: 'Gold', r: (a) => num(a.gold), right: true },
        { h: 'Bands', r: (a) => Object.entries(a.bands || {}).sort().map(([b, n]) => `${b}:${n}`).join(' ') || '—' },
        { h: 'Declined', r: (a) => <span className={a.minted >= 5 ? '' : 'text-gray-400'}>{a.minted}</span>, right: true }]} />
    </div>
  );
}

// ---- Evidence ----------------------------------------------------------------------
export function EvidenceBoard({ d }: { d: D }) {
  const bars = (o: Record<string, number>) => Object.entries(o || {}).map(([k, v]) => ({ k, v })).sort((a, b) => b.v - a.v);
  const metaObs = d.meta?.production_observations;
  return (
    <div className="space-y-4">
      <div className="grid gap-3 grid-cols-2 md:grid-cols-4">
        <Tile label="Atoms in the census" value={num(d.totals.atoms)} sub={d.meta?.census_at ? `census ${String(d.meta.census_at).slice(0, 16)}` : undefined} />
        <Tile label="Observations" value={num(d.totals.observations)} tone={metaObs != null && metaObs !== d.totals.observations ? 'warn' : undefined}
          sub={metaObs != null ? (metaObs === d.totals.observations ? 'matches the census meta (production)' : `census meta says ${num(metaObs)}`) : 'no census meta'} />
        <Tile label="Below the learner floor" value={num(d.totals.below_floor)} sub={`< ${d.evidence_floor} qualified`} tone={d.totals.below_floor ? 'warn' : undefined} />
        <Tile label="Generators" value={Object.keys(d.by_generator || {}).length} />
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        {([['By generator', d.by_generator], ['By source', d.by_source]] as [string, Record<string, number>][]).map(([title, o]) => (
          <div key={title}><H>{title}</H>
            <div className="h-64 rounded-lg border border-gray-200 dark:border-gray-700 p-2">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={bars(o)} layout="vertical" margin={{ left: 120 }}>
                  <XAxis type="number" fontSize={11} /><YAxis type="category" dataKey="k" fontSize={11} width={120} />
                  <Tooltip /><Bar dataKey="v" name="Observations" fill="#0ea5e9" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        ))}
      </div>
      {(d.outside_universe as [string, number][]).length > 0 && (
        <p className="text-xs text-gray-500">Outside the census universe (not counted): {(d.outside_universe as [string, number][]).map(([a, n]) => `${a} (${num(n)})`).join(', ')}.
          {d.meta?.probe_observations != null ? ` Probe observations: ${num(d.meta.probe_observations)} (never counted).` : ''}</p>)}
      <H>Per atom</H>
      <Table rows={d.atoms} max={400} cols={[
        { h: 'Atom', r: (x) => <code className="text-xs">{x.atom}</code> }, { h: 'Layer', r: (x) => x.layer },
        { h: 'Observations', r: (x) => num(x.observations), right: true }, { h: 'Learners', r: (x) => num(x.learners), right: true },
        { h: 'Qualified', r: (x) => <span className={x.below_floor ? 'text-red-600 font-medium' : ''}>{num(x.qualified)}</span>, right: true },
        { h: 'Grounded', r: (x) => num(x.grounded), right: true }, { h: 'Kind', r: (x) => x.construct_kind || '—' },
        { h: 'Known answers', r: (x) => x.kaa || 'not measured' }]} />
    </div>
  );
}

// ---- Registry and grounding --------------------------------------------------------
function HoldSwitch({ programId, hold, onChange }: { programId: string; hold: D; onChange: (h: D) => void }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const next = !hold.on;
  const go = async () => {
    setBusy(true); setError('');
    try {
      onChange(await trainingApi.setGenerationHold(programId, next, reason.trim()));
      setReason('');
    } catch (e: any) {
      setError(e?.message || 'The hold could not be switched.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mt-2 flex flex-wrap items-center gap-2">
      <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why (required)"
        className="px-2 py-1 text-xs border rounded min-w-[16rem] dark:bg-gray-700 dark:border-gray-600" />
      <button type="button" disabled={busy || !reason.trim()} onClick={go}
        className="px-2 py-1 text-xs rounded border border-gray-300 dark:border-gray-600 disabled:opacity-50">
        {next ? 'Put generation on hold' : 'Release the hold'}
      </button>
      <span className="text-xs text-gray-500">Needs training:approve; recorded as a new version with who and why.</span>
      {error ? <span className="text-xs text-red-600 w-full">{error}</span> : null}
    </div>
  );
}

export function RegistryBoard({ d }: { d: D }) {
  const [switched, setSwitched] = useState<D | null>(null);
  const hold = switched || d.hold || {};
  return (
    <div className="space-y-4">
      <div className={`rounded-lg border p-3 text-sm flex items-start gap-2 ${hold.on ? 'border-amber-300 bg-amber-50 dark:bg-amber-900/20' : 'border-emerald-300 bg-emerald-50 dark:bg-emerald-900/20'}`}>
        {hold.on ? <Lock className="w-4 h-4 text-amber-600 mt-0.5" /> : <ShieldOff className="w-4 h-4 text-emerald-600 mt-0.5" />}
        <span><strong>Generation hold: {hold.on ? 'ON' : 'off'}</strong>
          {hold.reason ? ` — ${hold.reason}` : ''}{hold.set_by ? ` (set by ${hold.set_by}${hold.set_at ? ', ' + String(hold.set_at).slice(0, 16) : ''})` : ''}
          {hold.note ? <span className="block text-xs text-gray-600">{hold.note}</span> : null}
          {d.programId ? <HoldSwitch programId={d.programId} hold={hold} onChange={setSwitched} /> : null}</span>
      </div>
      <div className="grid gap-3 grid-cols-2 md:grid-cols-4">
        {Object.entries(d.counts || {}).map(([k, v]) => <Tile key={k} label={`Cards ${k}`} value={num(v as number)} />)}
        <Tile label="Active cards grounded" value={num(d.grounded)} />
        <Tile label="Registry index" value={d.index?.status || 'none'} sub={d.index?.published_at ? `published ${String(d.index.published_at).slice(0, 10)}` : undefined} />
      </div>
      <H>Documents by type</H>
      <Table rows={Object.entries(d.doc_types || {}).map(([t, s]) => ({ id: t, t, s }))} cols={[{ h: 'Type', r: (x) => x.t },
        { h: 'By status', r: (x) => Object.entries(x.s).map(([k, v]) => `${k} ${v}`).join(' · ') }]} />
      <H>Attribute cards</H>
      <Table rows={d.attributes} max={700} cols={[{ h: 'Attribute', r: (x) => x.name }, { h: 'Status', r: (x) => x.status }, { h: 'Kind', r: (x) => x.kind || '—' },
        { h: 'Grounding', r: (x) => x.grounding || <span className="text-gray-400">none</span> }, { h: 'Observed by', r: (x) => x.observed_by, right: true }]} />
    </div>
  );
}

// ---- Teachers ----------------------------------------------------------------------
const CELL: Record<string, string> = { fit: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200', waived: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200', fail: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300', none: 'text-gray-400' };
export function TeachersBoard({ d }: { d: D }) {
  return (
    <div className="space-y-4">
      <p className="text-xs text-gray-500">A cell is <strong>fit</strong> with a valid pass on known answers; <strong>waived</strong> with an active waiver (never for unsafe); <strong>fail</strong> otherwise.</p>
      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
        <table className="text-sm">
          <thead className="bg-gray-50 dark:bg-gray-800 text-xs text-gray-500"><tr><th className="p-2 text-left">Teacher</th>{(d.construct_kinds as string[]).map((k) => <th key={k} className="p-2">{k}</th>)}</tr></thead>
          <tbody>{(d.matrix as D[]).map((row) => (
            <tr key={row.teacher} className="border-t border-gray-100 dark:border-gray-700">
              <td className="p-2 font-mono text-xs">{row.teacher}</td>
              {(d.construct_kinds as string[]).map((k) => { const c = row.cells[k]; return (
                <td key={k} className={`p-2 text-xs text-center ${CELL[c.state]}`} title={(c.entries || []).map((e: D) => `${e.population}: ${e.verdict}/${e.validity}`).join('\n')}>
                  {c.state}{c.fit_populations?.length ? <span className="block">{c.fit_populations.join(', ')}</span> : null}</td>); })}
            </tr>))}</tbody>
        </table>
      </div>
      <H>Waivers</H>
      <Table rows={d.waivers} cols={[{ h: 'Teacher', r: (x) => x.teacher }, { h: 'Kind', r: (x) => x.construct_kind }, { h: 'Waives', r: (x) => (x.waive || []).join(', ') },
        { h: 'Decided by', r: (x) => x.decided_by || '—' }, { h: 'Board', r: (x) => x.board || '—' }, { h: 'Status', r: (x) => x.withdrawn ? 'withdrawn' : (x.status || 'active') }]} />
    </div>
  );
}

/** Runs open over 24 h (FE-14 ST-9): an approver marks one lost, with why; it is never automatic. */
function StaleRuns({ rows }: { rows: D[] }) {
  const { programId = '' } = useParams<{ programId: string }>();
  const [done, setDone] = useState<Record<string, string>>({});
  const lost = async (id: string) => {
    const reason = window.prompt(`Why is ${id} lost? (e.g. its VM was released, no outputs)`);
    if (!reason) return;
    try { await trainingApi.markRunLost(programId, { runId: id, reason }); setDone({ ...done, [id]: 'marked lost' }); }
    catch (e: any) { setDone({ ...done, [id]: e?.message || 'not marked' }); }
  };
  return (
    <div><H>Open over 24 hours</H>
      <Table rows={rows} max={50} cols={[
        { h: 'Run', r: (x) => <code className="text-xs">{x.id}</code> }, { h: 'Stage', r: (x) => x.stage },
        { h: 'Started', r: (x) => String(x.started || '').replace('T', ' ').slice(0, 16) },
        { h: '', r: (x) => done[x.id] ? <span className="text-xs">{done[x.id]}</span>
          : <button type="button" onClick={() => lost(x.id)} className="text-xs text-accent" title="training:approve">Mark lost</button> }]} />
    </div>
  );
}

// ---- Process map -------------------------------------------------------------------
export function ProcessMapBoard({ d }: { d: D }) {
  const [open, setOpen] = useState('');
  const { programId = '' } = useParams<{ programId: string }>();
  const [st, setSt] = useState<D | null>(null);
  const [err, setErr] = useState('');
  useEffect(() => { trainingApi.steps(programId).then(setSt).catch(() => setSt(null)); }, [programId]);
  const stepOf = (id: string) => (st?.steps as D[] | undefined)?.find((x) => x.id === id);
  const move = async (id: string, stage: string) => {
    const note = window.prompt(`Why does ${id} move to ${stage}?`);
    if (!note) return;
    setErr('');
    try { await trainingApi.updateStep(programId, id, { stage, note }); setSt(await trainingApi.steps(programId)); }
    catch (e: any) { setErr(`${id}: ${e?.message || 'not moved'}`); }
  };
  const link = async (id: string, kind: string, cur: string) => {
    const url = window.prompt(`${kind} document for ${id} (path or URL)`, cur || '');
    if (url === null) return;
    try { await trainingApi.updateStep(programId, id, { docs: { [kind]: url } }); setSt(await trainingApi.steps(programId)); }
    catch (e: any) { setErr(`${id}: ${e?.message || 'not saved'}`); }
  };
  const bySteps = useMemo(() => {
    const m = new Map<string, D[]>();
    (d.activities as D[]).forEach((a) => { const k = `${a.track} · step ${a.step}`; m.set(k, [...(m.get(k) || []), a]); });
    return [...m.entries()].sort((a, b) => (a[1][0].step ?? 0) - (b[1][0].step ?? 0));
  }, [d.activities]);
  return (
    <div className="space-y-4">
      {err && <p className="text-xs text-red-600">{err}</p>}
      {st && <p className="text-xs text-gray-600 dark:text-gray-400">Design stages: {Object.entries(st.counts as Record<string, number>).filter(([, n]) => n).map(([k, n]) => `${n} ${k}`).join(' · ')}. Moving a step asks why; decided and accepted need training:approve.</p>}
      <div className="grid gap-3 grid-cols-2 md:grid-cols-4">
        <Tile label="Template" value={<code className="text-sm">{d.template}</code>} />
        <Tile label="Activities" value={d.counts.activities} sub={`${d.counts.with_items} with PMO items`} />
        {Object.entries(d.counts.by_status || {}).slice(0, 2).map(([k, v]) => <Tile key={k} label={k} value={v as number} />)}
      </div>
      {bySteps.map(([step, acts]) => (
        <div key={step}><H>{step}</H>
          <div className="grid gap-2 md:grid-cols-2 xl:grid-cols-3">
            {acts.map((a) => (
              <button key={a.id} type="button" onClick={() => setOpen(open === a.id ? '' : a.id)}
                className={`text-left rounded-lg border p-2 text-sm ${a.blocked_by_asks.length ? 'border-red-300' : 'border-gray-200 dark:border-gray-700'} hover:bg-gray-50 dark:hover:bg-gray-800`}>
                <div className="flex items-center gap-2"><code className="text-xs">{a.id}</code>
                  <span className="text-gray-900 dark:text-gray-100">{a.title}</span>
                  {a.blocked_by_asks.length ? <AlertTriangle className="w-3.5 h-3.5 text-red-600 ml-auto" /> : null}</div>
                {stepOf(a.id) && (
                  <div className="mt-1 flex flex-wrap items-center gap-2 text-xs" onClick={(e) => e.stopPropagation()}>
                    <select value={stepOf(a.id)!.stage} onChange={(e) => move(a.id, e.target.value)}
                      className="px-1 py-0.5 border rounded dark:bg-gray-700 dark:border-gray-600" title="Design stage (decided and accepted need training:approve)">
                      {(st!.stages as string[]).map((x) => <option key={x}>{x}</option>)}
                    </select>
                    {(['spec', 'design'] as const).map((k) => {
                      const v = stepOf(a.id)!.docs?.[k];
                      return v ? <a key={k} href={/^https?:/.test(v) ? v : undefined} title={v} className="text-accent underline">{k}</a>
                        : <span key={k} role="button" tabIndex={0} onClick={() => link(a.id, k, '')} className="text-gray-400 hover:text-accent">+{k}</span>;
                    })}
                    {stepOf(a.id)!.history?.length ? <span className="text-gray-400" title={stepOf(a.id)!.history.map((h: D) => `${h.at.slice(0, 10)} ${h.from}→${h.to} by ${h.by}: ${h.note}`).join(String.fromCharCode(10))}>{stepOf(a.id)!.history.length} move(s)</span> : null}
                  </div>)}
                <div className="text-xs text-gray-500 mt-0.5">{a.runnable ? 'integrated · ' : ''}{a.items.length} item(s)
                  {a.blocked_by_asks.length ? <span className="text-red-600"> · blocked by {a.blocked_by_asks.join(', ')}</span> : null}</div>
                {open === a.id && a.items.length > 0 && (
                  <ul className="mt-2 space-y-0.5 text-xs">{a.items.map((i: D) => <li key={i.id}><span className="text-gray-500">{i.key}</span> {i.title} · <em>{i.status}</em></li>)}</ul>)}
              </button>))}
          </div>
        </div>))}
    </div>
  );
}

// ---- Gates -------------------------------------------------------------------------
export function GatesBoard({ d }: { d: D }) {
  const [guard, setGuard] = useState('');
  const recent = (d.recent as D[]).filter((e) => !guard || e.gate === guard);
  return (
    <div className="space-y-4">
      <div className="grid gap-3 grid-cols-2 md:grid-cols-4">
        <Tile label={`Passed (${d.window_days} days)`} value={num(d.totals.passed)} />
        <Tile label={`Failed (${d.window_days} days)`} value={num(d.totals.failed)} tone={d.totals.failed ? 'warn' : undefined} />
        <Tile label="Guards seen" value={(d.by_guard as D[]).length} />
        <Tile label="Most-held atom" value={d.held_atoms[0]?.atom || '—'} sub={d.held_atoms[0] ? `${d.held_atoms[0].times} refusals` : undefined} />
      </div>
      <div className="h-48 rounded-lg border border-gray-200 dark:border-gray-700 p-2">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={d.by_day}>
            <CartesianGrid strokeDasharray="3 3" opacity={0.3} /><XAxis dataKey="day" fontSize={11} /><YAxis fontSize={11} /><Tooltip />
            <Bar dataKey="passed" stackId="a" fill="#16a34a" /><Bar dataKey="failed" stackId="a" fill="#dc2626" />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        <div><H>By guard (most failures first)</H>
          <Table rows={d.by_guard} cols={[
            { h: 'Guard', r: (x) => <button type="button" className="text-accent hover:underline" onClick={() => setGuard(guard === x.guard ? '' : x.guard)}>{x.guard}</button> },
            { h: 'Failed', r: (x) => x.failed, right: true }, { h: 'Passed', r: (x) => x.passed, right: true }]} /></div>
        <div><H>Most-held atoms</H>
          <Table rows={d.held_atoms} cols={[{ h: 'Atom', r: (x) => <code className="text-xs">{x.atom}</code> }, { h: 'Refusals', r: (x) => x.times, right: true }]} /></div>
      </div>
      <H>Recent events{guard ? ` · ${guard}` : ''}</H>
      <Table rows={recent} max={200} cols={[
        { h: 'At', r: (x) => String(x.at || '').replace('T', ' ').slice(0, 16) }, { h: 'Guard', r: (x) => x.gate },
        { h: 'Outcome', r: (x) => (x.passed ? <span className="text-emerald-600">pass</span> : <span className="text-red-600">fail</span>) },
        { h: 'Subject', r: (x) => x.subject || '—' }, { h: 'Reason', r: (x) => <span className="text-xs">{x.reason}</span> }]} />
    </div>
  );
}

// ---- Funnel ------------------------------------------------------------------------
/** The Compound stream's funnel: coverage per family, apart from atom gold (FE-13 ST-8). */
function CompoundCoverage({ d }: { d: D }) {
  const t = d.totals || {};
  return (
    <div className="space-y-3">
      <p className="text-xs text-gray-500">{t.families} families · {t.withGold} with compound gold · {t.gold} gold rows</p>
      <table className="w-full text-xs">
        <thead><tr className="text-left text-gray-500"><th className="p-1">Family</th><th className="p-1">Mode</th><th className="p-1 text-right">Gold</th><th className="p-1">By slot</th></tr></thead>
        <tbody>
          {(d.families as D[]).map((f) => (
            <tr key={f.family} className="border-t border-gray-100 dark:border-gray-700">
              <td className="p-1 font-mono">{f.family}</td><td className="p-1">{f.mode || '—'}</td>
              <td className={`p-1 text-right tabular-nums ${f.gold ? '' : 'text-amber-700'}`}>{f.gold}</td>
              <td className="p-1 text-gray-600 dark:text-gray-400">{Object.entries(f.slots || {}).map(([k, v]) => `${k}: ${v}`).join(' · ') || '—'}</td>
            </tr>))}
        </tbody>
      </table>
      {((d.notes as string[]) || []).map((n) => <p key={n} className="text-[11px] text-gray-500">{n}</p>)}
    </div>);
}

export function FunnelBoard({ d }: { d: D }) {
  if (d.unit === 'compound_family') return <CompoundCoverage d={d} />;
  return <AtomFunnel d={d} />;
}

function AtomFunnel({ d }: { d: D }) {
  const [layer, setLayer] = useState('');
  const [step, setStep] = useState('');
  const order = (d.steps as D[]).map((s) => s.step);
  const atoms = (d.atoms as D[]).filter((a) => !layer || a.layer === layer);
  const counts = (d.steps as D[]).map((s) => ({
    ...s, atoms: layer ? atoms.filter((a) => order.indexOf(a.step) >= order.indexOf(s.step)).length : s.atoms,
  }));
  return (
    <div className="space-y-4">
      <select value={layer} onChange={(e) => setLayer(e.target.value)} className="px-2 py-1 text-xs border rounded dark:bg-gray-700 dark:border-gray-600">
        <option value="">All layers</option>{(d.layers as string[]).map((x) => <option key={x}>{x}</option>)}
      </select>
      <div className="h-72 rounded-lg border border-gray-200 dark:border-gray-700 p-2">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={counts} layout="vertical" margin={{ left: 140 }}>
            <XAxis type="number" fontSize={11} /><YAxis type="category" dataKey="label" fontSize={11} width={140} /><Tooltip />
            <Bar dataKey="atoms" name="Atoms" fill="#8b5cf6" onClick={(p: any) => setStep(p?.step || p?.payload?.step || '')} />
          </BarChart>
        </ResponsiveContainer>
      </div>
      <div className="flex flex-wrap gap-1">
        {(d.steps as D[]).map((s) => (
          <button key={s.step} type="button" onClick={() => setStep(step === s.step ? '' : s.step)}
            className={`px-2 py-0.5 text-xs rounded border ${step === s.step ? 'border-accent text-accent' : 'border-gray-300 dark:border-gray-600'}`}>
            stopped at {s.label}
          </button>))}
      </div>
      <ul className="text-xs text-gray-500 list-disc pl-5">{(d.notes as string[]).map((n) => <li key={n}>{n}</li>)}</ul>
      {step && (<>
        <H>Atoms stopped at: {(d.steps as D[]).find((s) => s.step === step)?.label}</H>
        <Table rows={atoms.filter((a) => a.step === step)} cols={[
          { h: 'Atom', r: (x) => <code className="text-xs">{x.ref}</code> }, { h: 'Layer', r: (x) => x.layer },
          { h: 'Family', r: (x) => x.family || '—' },
          { h: 'Next step', r: (x) => (d.steps as D[]).find((s) => s.step === x.next)?.label || 'done' }]} />
      </>)}
    </div>
  );
}

// ---- Ontology ----------------------------------------------------------------------
export function OntologyBoard({ d }: { d: D }) {
  const [open, setOpen] = useState('');
  const keys = ['apps', 'compounds', 'elements', 'atoms', 'unlinked'];
  return (
    <div className="space-y-4">
      <div className="grid gap-3 grid-cols-2 md:grid-cols-5">
        {keys.map((k) => <Tile key={k} label={k} value={num(d.counts[k])} tone={k === 'unlinked' && d.counts[k] ? 'warn' : undefined} />)}
      </div>
      {(d.apps as D[]).map((app) => (
        <div key={app.key} className="rounded-lg border border-gray-200 dark:border-gray-700">
          <button type="button" onClick={() => setOpen(open === app.key ? '' : app.key)} className="w-full text-left p-3 flex items-center gap-2">
            <span className="font-medium text-gray-900 dark:text-gray-100">{app.name}</span><code className="text-xs text-gray-500">{app.key}</code>
            <span className="ml-auto text-xs text-gray-500">{app.compounds.length} compound(s)</span>
          </button>
          {open === app.key && (
            <div className="px-3 pb-3 space-y-2 text-sm">
              {app.purpose ? <p className="text-xs text-gray-600 dark:text-gray-400">{app.purpose}</p> : null}
              {(app.compounds as D[]).map((c) => (
                <div key={c.id}>
                  <div className="font-medium">{c.name || c.id} <span className="text-xs text-gray-500">{c.layer}</span></div>
                  {(c.phases as D[]).map((ph) => (
                    <div key={ph.name} className="ml-3 mt-1">
                      <div className="text-xs uppercase text-gray-500">{ph.name}</div>
                      {(ph.elements as D[]).map((el) => (
                        <div key={el.id} className="ml-3 text-xs">
                          <span className="text-gray-800 dark:text-gray-200">{el.name}</span>
                          <span className="text-gray-500"> · {el.atoms.length} atom(s){el.unresolved?.length ? `, ${el.unresolved.length} unresolved` : ''}</span>
                        </div>))}
                    </div>))}
                </div>))}
            </div>)}
        </div>))}
      {d.counts.unlinked > 0 && (<>
        <H>Released atoms no element lists</H>
        <div className="text-xs space-y-1">
          {Object.entries(d.unlinked as Record<string, string[]>).map(([l, refs]) => <div key={l}><strong>{l}</strong>: {refs.join(', ')}</div>)}
        </div>
      </>)}
    </div>
  );
}

export const BOARD_VIEW: Record<string, (p: { d: D }) => React.ReactElement> = {
  runs: RunsBoard, models: ModelsBoard, atoms: AtomsBoard, evidence: EvidenceBoard,
  registry: RegistryBoard, teachers: TeachersBoard, 'process-map': ProcessMapBoard,
  gates: GatesBoard, funnel: FunnelBoard, ontology: OntologyBoard,
};
